
import React, { useState, useEffect, Fragment } from "react";
import { MaterialReactTable } from "material-react-table";
import { Breadcrumb, Col, Row } from "reactstrap";
import { Box } from "@mui/material";
import { formattedDateOnly } from "../../../../src/utilities/formatedDate";
import GetButtons from "../../common/getButtons";
import convertToRupees from "../../../../src/utilities/convertToRupees";
import { getAllPartnershipListBYRoleWithoutPagination, SalesLedgerList } from "../../../api";
import GetExport from "../../common/getExport";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./invoiceLedger.css";

/* ─── tiny inline SVG icons ─── */
const IconLedger = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
    </svg>
);

const IconCredit = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5" />
        <polyline points="5 12 12 5 19 12" />
    </svg>
);

const IconDebit = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="19 12 12 19 5 12" />
    </svg>
);

const IconBalance = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
);

const IconFilter = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);

/* ─── helper to detect wallet type ─── */
const isCredit = (walletType) =>
    walletType && (walletType.toLowerCase().includes("credit") || walletType.toLowerCase() === "cr");
const isDebit = (walletType) =>
    walletType && (walletType.toLowerCase().includes("debit") || walletType.toLowerCase() === "dr");

/* ─── Amount cell renderer ─── */
const AmountCell = ({ value, type }) => {
    if (!value || value === "N/A") return <span className="cell-na">—</span>;
    if (type === "credit") return <span className="cell-credit">{value}</span>;
    if (type === "debit") return <span className="cell-debit">{value}</span>;
    if (type === "balance") return <span className="cell-balance">{value}</span>;
    return <span className="cell-amount">{value}</span>;
};

/* ─── Wallet type badge ─── */
const WalletBadge = ({ walletType }) => {
    if (!walletType || walletType === "N/A") return <span className="cell-na">—</span>;
    const cls = isCredit(walletType) ? "credit" : isDebit(walletType) ? "debit" : "default";
    return <span className={`wallet-badge ${cls}`}>{walletType}</span>;
};

/* ════════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════════ */
const InvoiceLedger = () => {
    const [selectedBP, setSelectedBP] = useState();
    const [partners, setPartners] = useState([]);
    const [ledgerData, setLedgerData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState([{ id: "model_id", desc: true }]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [columnFilters, setColumnFilters] = useState([]);
    const debouncedSetColumnFilters = debounce(setColumnFilters, 500);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const currentYear = new Date().getFullYear();
    const [startYear, setStartYear] = useState(currentYear);
    const [endYear, setEndYear] = useState(currentYear);
    const [error, setError] = useState("");
    const [role, setRole] = useState(null);

    /* ── Summary totals derived from ledger data ── */
    const totalCredit = ledgerData.reduce((acc, r) => {
        if (isCredit(r.walletType)) acc += parseFloat(r.grossReceivable || 0);
        return acc;
    }, 0);

    const totalDebit = ledgerData.reduce((acc, r) => {
        if (isDebit(r.walletType)) acc += parseFloat(r.grossReceivable || 0);
        return acc;
    }, 0);

    const totalBalance = ledgerData.length
        ? parseFloat(ledgerData[0]?.balance || 0)
        : 0;

    useEffect(() => {
        const roleData = JSON.parse(localStorage.getItem("ROLE"));
        const userData = JSON.parse(localStorage.getItem("user"));
        if (roleData) setRole(roleData.key);
        if (userData) setSelectedBP(userData.partnership_id);
    }, []);

    const handleStartYearChange = (date) => {
        if (date && date.getFullYear() > currentYear) {
            setError(`Start year cannot be greater than ${currentYear}.`);
            return;
        }
        setStartYear(date);
        if (endYear && date && endYear.getFullYear() < date.getFullYear()) {
            setError("End year cannot be earlier than start year.");
        } else {
            setError("");
        }
    };

    const handleEndYearChange = (date) => {
        if (date && date.getFullYear() > currentYear) {
            setError(`End year cannot be greater than ${currentYear}.`);
            return;
        }
        if (startYear && date && date.getFullYear() < startYear.getFullYear()) {
            setError("End year cannot be earlier than start year.");
            return;
        }
        setEndYear(date);
        setError("");
    };

    const fetchBussinessPartnerships = async () => {
        try {
            const response = await getAllPartnershipListBYRoleWithoutPagination();
            if (response.status === 200) {
                const res = response?.data?.result?.partnershipData;
                const partnershipOptions = [];
                res.map((item) => {
                    const pcode =
                        item?.partnership_code?.length > 15
                            ? item?.partnership_code?.slice(0, 15) + "..."
                            : item?.partnership_code;
                    const obj = {
                        value: item.partnership_id,
                        label: `${item.name} (${pcode})`,
                        user_name: item?.user?.name || "",
                        is_active: item.is_active,
                        is_kyc_active: item.is_kyc_active,
                        wallet_closure_status: item.wallet_closure_status,
                        wallet_balance: item.user?.user_wallet?.amount || 0,
                        msc_percentage: item.commission_percent || 0,
                        partner_info: item || {},
                    };
                    if (
                        obj.is_active &&
                        obj.is_active !== false &&
                        obj.is_kyc_active &&
                        obj.is_kyc_active !== false &&
                        obj.wallet_balance > 0
                    ) {
                        partnershipOptions.push(obj);
                    }
                });
                setPartners(partnershipOptions);
            }
        } catch (error) {
            console.error("Error fetching business partnerships", error);
            toast.error("Failed to load business partnerships.");
        }
    };

    const fetchLedgerData = async () => {
        try {
            setIsLoading(true);
            const data = {
                partnershipId: selectedBP,
                dateRange: `${startYear || ""}-${endYear || ""}`,
                page: pagination.pageIndex,
                limit: pagination.pageSize,
                filters: columnFilters,
                sorting,
                globalFilter,
            };
            const response = await SalesLedgerList(data);
            const ld = response?.data?.result?.data;
            setLedgerData(ld || []);
            setCount(response?.data?.result?.pagination?.totalCount);
        } catch (error) {
            console.error("Error fetching ledger data:", error);
            toast.error("Failed to load ledger data.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBussinessPartnerships();
        if (selectedBP && startYear && endYear) {
            fetchLedgerData();
        }
    }, [pagination, selectedBP, startYear, endYear, columnFilters, sorting, globalFilter]);

    /* ─────────── Columns ─────────── */
    const financialColumns = [
        {
            accessorKey: "date",
            header: "Date",
            enableClickToCopy: false,
            enableSorting: false,
            size: 100,
            Cell: ({ row }) => (
                <span style={{ fontWeight: 500, color: "#374151", fontSize: "0.8rem" }}>
                    {formattedDateOnly(row.original.created_at) || <span className="cell-na">—</span>}
                </span>
            ),
        },
        {
            accessorKey: "description",
            header: "Description",
            enableClickToCopy: false,
            enableSorting: false,
            size: 160,
            Cell: ({ row }) =>
                row.original.description ? (
                    <span style={{ fontSize: "0.8rem", color: "#374151" }}>{row.original.description}</span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "wallet_type",
            header: "Wallet Type",
            enableClickToCopy: false,
            enableSorting: false,
            size: 120,
            Cell: ({ row }) => <WalletBadge walletType={row.original.walletType} />,
        },
        {
            accessorKey: "bp_code",
            header: "BP Code",
            enableClickToCopy: false,
            enableSorting: false,
            size: 100,
            Cell: ({ row }) =>
                row.original.bpCode ? (
                    <span style={{ fontFamily: "monospace", fontSize: "0.78rem", color: "#4f46e5", fontWeight: 600 }}>
                        {row.original.bpCode}
                    </span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "bp_name",
            header: "BP Name",
            enableClickToCopy: false,
            enableSorting: false,
            size: 140,
            Cell: ({ row }) =>
                row.original.bpName ? (
                    <span style={{ fontSize: "0.8rem", fontWeight: 500 }}>{row.original.bpName}</span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "invoice_number",
            header: "Invoice #",
            enableClickToCopy: false,
            enableSorting: false,
            size: 110,
            Cell: ({ row }) =>
                row.original.invoiceNumber ? (
                    <span style={{ fontFamily: "monospace", fontSize: "0.78rem", color: "#6366f1", fontWeight: 600 }}>
                        {row.original.invoiceNumber}
                    </span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "coa_number",
            header: "COA #",
            enableClickToCopy: false,
            enableSorting: false,
            size: 100,
            Cell: ({ row }) =>
                row.original.coaNumber ? (
                    <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{row.original.coaNumber}</span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "receipt_number",
            header: "BR Receipt #",
            enableClickToCopy: false,
            enableSorting: false,
            size: 120,
            Cell: ({ row }) =>
                row.original.offlineReceiptNumber ? (
                    <span style={{ fontSize: "0.78rem" }}>{row.original.offlineReceiptNumber}</span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "utr",
            header: "Payment UTR",
            enableClickToCopy: false,
            enableSorting: false,
            size: 130,
            Cell: ({ row }) =>
                row.original.paymentUtr ? (
                    <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{row.original.paymentUtr}</span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "coa_receivable",
            header: "COA Receivable",
            enableClickToCopy: false,
            enableSorting: false,
            size: 130,
            Cell: ({ row }) => (
                <AmountCell
                    value={convertToRupees(row.original.coaReceivable)}
                    type={
                        isCredit(row.original.walletType)
                            ? "credit"
                            : isDebit(row.original.walletType)
                                ? "debit"
                                : "neutral"
                    }
                />
            ),
        },
        {
            accessorKey: "gross_receivable",
            header: "Gross Receivable",
            enableClickToCopy: false,
            enableSorting: false,
            size: 140,
            Cell: ({ row }) => (
                <AmountCell
                    value={convertToRupees(row.original.grossReceivable)}
                    type={
                        isCredit(row.original.walletType)
                            ? "credit"
                            : isDebit(row.original.walletType)
                                ? "debit"
                                : "neutral"
                    }
                />
            ),
        },
        {
            accessorKey: "msc",
            header: "MSC",
            enableClickToCopy: false,
            enableSorting: false,
            size: 100,
            Cell: ({ row }) => (
                <AmountCell value={convertToRupees(row.original.msc)} type="neutral" />
            ),
        },
        {
            accessorKey: "tds",
            header: "TDS",
            enableClickToCopy: false,
            enableSorting: false,
            size: 100,
            Cell: ({ row }) => (
                <AmountCell value={convertToRupees(row.original.tds)} type="debit" />
            ),
        },
        {
            accessorKey: "receipt",
            header: "Receipt",
            enableClickToCopy: false,
            enableSorting: false,
            size: 110,
            Cell: ({ row }) => (
                <AmountCell value={convertToRupees(row.original.receiptAmount)} type="credit" />
            ),
        },
        {
            accessorKey: "net_receivable",
            header: "Net Receivable",
            enableClickToCopy: false,
            enableSorting: false,
            size: 130,
            Cell: ({ row }) => (
                <AmountCell
                    value={convertToRupees(row.original.netReceivable)}
                    type={
                        isCredit(row.original.walletType)
                            ? "credit"
                            : isDebit(row.original.walletType)
                                ? "debit"
                                : "neutral"
                    }
                />
            ),
        },
        {
            accessorKey: "remaining_amount",
            header: "Remaining Amt",
            enableClickToCopy: false,
            enableSorting: false,
            size: 130,
            Cell: ({ row }) => (
                <AmountCell value={convertToRupees(row.original.remainingAmount)} type="neutral" />
            ),
        },
        {
            accessorKey: "reference",
            header: "Reference",
            enableClickToCopy: false,
            enableSorting: false,
            size: 130,
            Cell: ({ row }) =>
                row.original.reference ? (
                    <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>{row.original.reference}</span>
                ) : (
                    <span className="cell-na">—</span>
                ),
        },
        {
            accessorKey: "balance",
            header: "Balance",
            size: 120,
            Cell: ({ row }) => (
                <AmountCell value={convertToRupees(row.original.balance)} type="balance" />
            ),
        },
    ];

    /* ─────────── Export data ─────────── */
    const exportedFinancialData = ledgerData.map((item) => ({
        Date: item.created_at ? formattedDateOnly(item.created_at) : "N/A",
        Description: item.description || "N/A",
        Wallet_Type: item.walletType || "N/A",
        BP_Code: item.bpCode || "N/A",
        BP_Name: item.bpName || "N/A",
        Invoice_Number: item.invoiceNumber || "N/A",
        COA_Number: item.coaNumber || "N/A",
        Receipt_Number: item.offlineReceiptNumber || "N/A",
        Payment_UTR: item.paymentUtr || "N/A",
        COA_Receivable: item.coaReceivable ? convertToRupees(item.coaReceivable, true) : "N/A",
        Gross_Receivable: item.grossReceivable ? convertToRupees(item.grossReceivable, true) : "N/A",
        MSC: item.msc ? convertToRupees(item.msc, true) : "N/A",
        TDS: item.tds ? convertToRupees(item.tds, true) : "N/A",
        Receipt: item.receiptAmount ? convertToRupees(item.receiptAmount, true) : "N/A",
        Net_Receivable: item.netReceivable ? convertToRupees(item.netReceivable, true) : "N/A",
        Remaining_Amount: item.remainingAmount ? convertToRupees(item.remainingAmount, true) : "N/A",
        Balance: item.balance ? convertToRupees(item.balance, true) : "N/A",
        Reference: item.reference || "N/A",
    }));

    /* ─────────── Render ─────────── */
    return (
        <Fragment>
            <Breadcrumb parent="Home" title="Invoice Ledger" />

            <div className="ledger-page-wrapper">
                {/* ── Header ── */}
                <div className="ledger-header">
                    <div className="ledger-header-left">
                        <div className="ledger-header-icon">
                            <IconLedger />
                        </div>
                        <div>
                            <h1 className="ledger-title">Invoice Ledger</h1>
                            <p className="ledger-subtitle">Track credits, debits and outstanding balances</p>
                        </div>
                    </div>
                </div>

                {/* ── Unified Widget Row: Filters + Summary Cards ── */}
                <div className="ledger-widget-row">

                    {/* Filter widget */}
                    <div className="widget-filter-card">
                        <div className="widget-filter-header">
                            <IconFilter />
                            <span>Filters</span>
                        </div>
                        <div className="widget-filter-body">
                            {role !== "DEALERSHIP_MANAGER" && (
                                <div className="widget-field">
                                    <label className="ledger-form-label">
                                        Business Partner <sup>*</sup>
                                    </label>
                                    <select
                                        className="ledger-select"
                                        value={selectedBP || ""}
                                        onChange={(e) => {
                                            setSelectedBP(e.target.value);
                                            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                                        }}
                                    >
                                        <option value="">Select Partner</option>
                                        {partners?.map((ele, index) => (
                                            <option key={index} value={ele.value}>
                                                {ele.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="widget-field">
                                <label className="ledger-form-label">
                                    Year Range <sup>*</sup>
                                </label>
                                <div className="year-pickers-row">
                                    <DatePicker
                                        selected={startYear ? new Date(startYear, 0, 1) : null}
                                        onChange={handleStartYearChange}
                                        showYearPicker
                                        dateFormat="yyyy"
                                        placeholderText="Start Year"
                                        maxDate={new Date(currentYear, 11, 31)}
                                    />
                                    <span className="year-separator">–</span>
                                    <DatePicker
                                        selected={endYear ? new Date(endYear, 0, 1) : null}
                                        onChange={handleEndYearChange}
                                        showYearPicker
                                        dateFormat="yyyy"
                                        placeholderText="End Year"
                                        maxDate={new Date(currentYear, 11, 31)}
                                    />
                                </div>
                                {error && <div className="year-error">⚠ {error}</div>}
                            </div>
                        </div>
                    </div>

                    {/* Total Credit */}
                    <div className="summary-card credit">
                        <div className="summary-card-icon"><IconCredit /></div>
                        <div className="summary-card-info">
                            <div className="summary-label">Total Credit</div>
                            <div className="summary-value">
                                {ledgerData.length > 0 ? convertToRupees(totalCredit) || "₹0" : "₹0"}
                            </div>
                            <span className="summary-badge">Incoming</span>
                        </div>
                    </div>

                    {/* Total Debit */}
                    <div className="summary-card debit">
                        <div className="summary-card-icon"><IconDebit /></div>
                        <div className="summary-card-info">
                            <div className="summary-label">Total Debit</div>
                            <div className="summary-value">
                                {ledgerData.length > 0 ? convertToRupees(totalDebit) || "₹0" : "₹0"}
                            </div>
                            <span className="summary-badge">Outgoing</span>
                        </div>
                    </div>

                    {/* Closing Balance */}
                    <div className="summary-card balance">
                        <div className="summary-card-icon"><IconBalance /></div>
                        <div className="summary-card-info">
                            <div className="summary-label">Closing Balance</div>
                            <div className="summary-value">
                                {ledgerData.length > 0 ? convertToRupees(totalBalance) || "₹0" : "₹0"}
                            </div>
                            <span className="summary-badge">Latest</span>
                        </div>
                    </div>

                </div>

                {/* ── Ledger Table ── */}
                <div className="ledger-table-wrapper">
                    <MaterialReactTable
                        data={ledgerData || []}
                        columns={financialColumns || []}
                        enableGlobalFilter={false}
                        initialState={{ showColumnFilters: false, density: "compact" }}
                        positionToolbarAlertBanner="bottom"
                        defaultColumn={{
                            maxSize: 400,
                            minSize: 80,
                            size: 100,
                        }}
                        renderTopToolbarCustomActions={() => (
                            <Box sx={{ display: "flex", gap: "0.5rem", p: "0.5rem", flexWrap: "wrap" }}>
                                <GetButtons onClick={fetchLedgerData} type="refresh" />
                                <GetExport
                                    columns={financialColumns}
                                    exportedData={exportedFinancialData}
                                    name="Invoice_Ledger"
                                />
                            </Box>
                        )}
                        columnResizeMode="onChange"
                        manualFiltering
                        manualPagination
                        manualSorting
                        onColumnFiltersChange={debouncedSetColumnFilters}
                        onGlobalFilterChange={setGlobalFilter}
                        onPaginationChange={setPagination}
                        onSortingChange={setSorting}
                        muiTableBodyProps={{
                            sx: {
                                "& td": {
                                    paddingTop: "8px",
                                    paddingBottom: "8px",
                                },
                            },
                        }}
                        rowCount={count}
                        state={{
                            columnFilters,
                            globalFilter,
                            isLoading,
                            pagination,
                            sorting,
                            showLoadingOverlay: false,
                            showProgressBars: isLoading,
                        }}
                        enableFacetedValues={true}
                    />
                </div>
            </div>
        </Fragment>
    );
};

export default InvoiceLedger;